/**
 * useAgenticChat — the ONE WebSocket hook for every chat surface (TASK-710).
 *
 * Replaces useKYCChatWebSocket / usePrepChatWebSocket / useWorkbenchChatWebSocket
 * (77–78% line-identical) with their behavioral superset: /ws/chat transport with
 * auth-token URL, 30s ping keepalive, exponential-backoff reconnect (1s→30s ×5)
 * with token re-read before every retry, `isReconnecting`, hard-gate confirm
 * send, and a declared chunk-handler map.
 *
 * ## Chunk contract
 * Core types (`token`/`done`/`error`) are handled internally. `widget` defaults
 * to the canvasStore sink (OpenDocument → pending-open slot, everything else →
 * addSpecs) unless the caller supplies its own handler. EVERY other chunk type
 * must appear in `handlers` or `ignored` — an undeclared type is reported via
 * `onUnhandledChunk` (default: console warning). Silent drops were the bug
 * class this replaces (GenAI dropped 11 of 19 chunk types silently).
 * The per-scope declarations are asserted against the full contract in
 * types/chunkContract.test.ts.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { API_CONFIG } from '../config/api-config';
import { logger } from '../utils/logger';
import { useCanvasStore, openDocumentFromWidget } from '../stores/canvasStore';
import type { StreamingChunkType } from '../types/chat.types';

// ============================================================================
// Types
// ============================================================================

/** TASK-457: a hard-gated action awaiting explicit human confirm. */
export interface HardGateConfirm {
  action_id: string;
  tool_name: string;
  risk?: string;
  summary?: string;
  args_preview?: Record<string, unknown>;
}

export interface AgenticChunk {
  type: StreamingChunkType;
  content?: string;
  status?: string;
  metadata?: {
    conversation_id?: string;
    message_id?: string;
    query_time_ms?: number;
  };
  error?: string;
  confirm?: HardGateConfirm;
  widget?: { component?: string; props?: Record<string, unknown> };
  /** Domain payloads are keyed by the chunk type (e.g. chunk.spreadsheet_edit). */
  [key: string]: unknown;
}

export type ChunkHandlerMap = Partial<
  Record<StreamingChunkType, (chunk: AgenticChunk) => void>
>;

export interface UseAgenticChatOptions {
  /** Label used in log lines, e.g. the scope key. */
  logLabel: string;
  onMessageComplete: (content: string, conversationId: string) => void;
  onError: (error: string) => void;
  /** Per-chunk-type handlers beyond the internal core. */
  handlers: ChunkHandlerMap;
  /** Chunk types this scope deliberately does not consume. */
  ignored?: StreamingChunkType[];
  /** Called for a chunk type that is neither handled, ignored, nor core. */
  onUnhandledChunk?: (type: string) => void;
  /** Noun used in connect-failure copy, e.g. 'spreadsheet'. Default 'assistant'. */
  assistantNoun?: string;
}

export interface UseAgenticChatReturn {
  isConnected: boolean;
  isReconnecting: boolean;
  isStreaming: boolean;
  streamingContent: string;
  sendMessage: (
    message: string,
    conversationId?: string | null,
    context?: Record<string, unknown>
  ) => boolean;
  sendConfirm: (
    actionId: string,
    decision: 'confirm' | 'cancel',
    conversationId?: string | null
  ) => boolean;
  connect: () => void;
  disconnect: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const PING_INTERVAL = 30_000;
const RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;
const MAX_RECONNECT_ATTEMPTS = 5;

/** Default `widget` sink: OpenDocument → pending-open slot, else canvas tile. */
export const canvasWidgetHandler = (chunk: AgenticChunk): void => {
  const widget = chunk.widget;
  if (!widget?.component) return;
  if (widget.component === 'OpenDocument') {
    // TASK-796: one shared mapping, so the task scope cannot reach the workbench
    // through one sink and be dropped by the other.
    openDocumentFromWidget(widget.props);
    return;
  }
  useCanvasStore
    .getState()
    .addSpecs([{ component: widget.component, props: widget.props ?? {} }]);
};

/**
 * The registry key for a creation-result card. TASK-790: one constant, because
 * this string is the hinge of the TASK-742/768 interception and was written out
 * at three sites (both interceptors and `registry.ts`) — rename the registry
 * entry and the two interceptors go silently blind, sending every generated
 * document back to the canvas sink.
 */
export const ARTIFACT_CARD_COMPONENT = 'ArtifactCard';

/**
 * TASK-790: the `widget` interceptor that keeps creation results IN the
 * conversation that produced them, instead of letting them fall through to the
 * canvas sink and auto-open a slide-over over the transcript.
 *
 * TASK-742 fixed this in the workbench rail; TASK-768 ported the same six lines
 * into the dock. Two copies, differing only in where the card is put — which is
 * what a callback parameter is for. The rail keeps cards in component state, the
 * dock attaches them to the message (it persists its transcript and switches
 * scope; component state survives neither), so `onCard` is the whole difference.
 *
 * Everything that is not a card still reaches `canvasWidgetHandler`, so
 * TASK-582's `open_view` contract is untouched: the match is on the component,
 * never on the route.
 */
export const makeArtifactCardInterceptor =
  <TProps,>(onCard: (props: TProps) => void) =>
  (chunk: AgenticChunk): void => {
    if (chunk.widget?.component === ARTIFACT_CARD_COMPONENT) {
      onCard((chunk.widget?.props ?? {}) as TProps);
      return;
    }
    canvasWidgetHandler(chunk);
  };

// ============================================================================
// Hook
// ============================================================================

export function useAgenticChat({
  logLabel,
  onMessageComplete,
  onError,
  handlers,
  ignored = [],
  onUnhandledChunk,
  assistantNoun = 'assistant',
}: UseAgenticChatOptions): UseAgenticChatReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');

  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const streamingContentRef = useRef('');

  // Callbacks/config in refs — no stale closures, no reconnect churn.
  const onMessageCompleteRef = useRef(onMessageComplete);
  const onErrorRef = useRef(onError);
  const handlersRef = useRef(handlers);
  const ignoredRef = useRef(new Set<string>(ignored));
  const onUnhandledChunkRef = useRef(onUnhandledChunk);
  useEffect(() => {
    onMessageCompleteRef.current = onMessageComplete;
    onErrorRef.current = onError;
    handlersRef.current = handlers;
    ignoredRef.current = new Set(ignored);
    onUnhandledChunkRef.current = onUnhandledChunk;
  }, [onMessageComplete, onError, handlers, ignored, onUnhandledChunk]);

  const getAuthToken = useCallback(
    (): string | null => localStorage.getItem('auth_token'),
    []
  );

  const getWebSocketUrl = useCallback((): string => {
    const wsUrl =
      API_CONFIG.WS_GATEWAY_URL ||
      API_CONFIG.GATEWAY_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const token = getAuthToken();
    return `${wsUrl}/ws/chat${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  }, [getAuthToken]);

  const stopPing = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const stopReconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const startPing = useCallback(() => {
    stopPing();
    pingTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // Ping failed — the close handler takes over.
        }
      }
    }, PING_INTERVAL);
  }, [stopPing]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    stopPing();
    stopReconnect();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
    setIsReconnecting(false);
    setIsStreaming(false);
    streamingContentRef.current = '';
    setStreamingContent('');
    reconnectAttemptsRef.current = 0;
  }, [stopPing, stopReconnect]);

  const handleChunk = useCallback((chunk: AgenticChunk) => {
    // Gateway keepalive reply to our ping — transport-level, deliberately NOT
    // part of the StreamingChunkType contract (see chunkContracts tripwires).
    if ((chunk.type as string) === 'pong') return;

    switch (chunk.type) {
      case 'token':
        setIsStreaming(true);
        streamingContentRef.current += chunk.content || '';
        setStreamingContent(streamingContentRef.current);
        return;

      case 'done': {
        const convId = chunk.metadata?.conversation_id || '';
        const finalContent = streamingContentRef.current;
        streamingContentRef.current = '';
        setStreamingContent('');
        setIsStreaming(false);
        onMessageCompleteRef.current(finalContent, convId);
        return;
      }

      case 'error':
        streamingContentRef.current = '';
        setStreamingContent('');
        setIsStreaming(false);
        onErrorRef.current(chunk.error || 'Unknown streaming error');
        return;
    }

    const handler = handlersRef.current[chunk.type];
    if (handler) {
      handler(chunk);
      return;
    }

    // Widget default: the canvasStore is the single sink (TASK-492 architecture).
    if (chunk.type === 'widget') {
      canvasWidgetHandler(chunk);
      return;
    }

    if (ignoredRef.current.has(chunk.type)) return;

    if (onUnhandledChunkRef.current) {
      onUnhandledChunkRef.current(chunk.type);
    } else {
      logger.warn(`[${logLabel}] Undeclared chunk type dropped: ${chunk.type}`);
    }
  }, [logLabel]);

  const attemptReconnectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      onErrorRef.current('Not authenticated');
      return;
    }

    intentionalCloseRef.current = false;

    try {
      const ws = new WebSocket(getWebSocketUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsReconnecting(false);
        reconnectAttemptsRef.current = 0;
        startPing();
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          handleChunk(JSON.parse(event.data) as AgenticChunk);
        } catch (err) {
          logger.error(`[${logLabel}] Failed to parse message:`, err);
        }
      };

      ws.onclose = (event: CloseEvent) => {
        setIsConnected(false);
        stopPing();
        if (intentionalCloseRef.current) return;
        if (event.code === 1000 || event.code === 1001) return;
        attemptReconnectRef.current();
      };

      ws.onerror = () => {
        // The close event follows and owns reconnection.
      };
    } catch (err) {
      logger.error(`[${logLabel}] Failed to create WebSocket:`, err);
      onErrorRef.current(`Failed to connect to the ${assistantNoun}`);
    }
  }, [getAuthToken, getWebSocketUrl, startPing, stopPing, handleChunk, logLabel, assistantNoun]);

  const attemptReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setIsReconnecting(false);
      onErrorRef.current('Failed to reconnect after multiple attempts');
      return;
    }
    setIsReconnecting(true);

    reconnectAttemptsRef.current++;
    const delay = Math.min(
      RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
      MAX_RECONNECT_DELAY
    );

    reconnectTimerRef.current = setTimeout(() => {
      // Re-read the token — it may have been refreshed (or revoked) meanwhile.
      const token = getAuthToken();
      if (!token) {
        logger.warn(`[${logLabel}] Reconnect aborted — no auth token in localStorage`);
        setIsReconnecting(false);
        onErrorRef.current('Session expired — please log in again');
        return;
      }
      connect();
    }, delay);
  }, [connect, getAuthToken, logLabel]);
  attemptReconnectRef.current = attemptReconnect;

  const sendMessage = useCallback(
    (
      message: string,
      conversationId?: string | null,
      context?: Record<string, unknown>
    ): boolean => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return false;
      }
      try {
        wsRef.current.send(
          JSON.stringify({
            type: 'message',
            payload: {
              message,
              conversation_id: conversationId || undefined,
              context,
            },
          })
        );
        setIsStreaming(true);
        streamingContentRef.current = '';
        setStreamingContent('');
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const sendConfirm = useCallback(
    (
      actionId: string,
      decision: 'confirm' | 'cancel',
      conversationId?: string | null
    ): boolean => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return false;
      }
      try {
        wsRef.current.send(
          JSON.stringify({
            type: 'confirm_action',
            payload: {
              action_id: actionId,
              decision,
              conversation_id: conversationId || undefined,
            },
          })
        );
        if (decision === 'confirm') {
          // The confirmed action replays and streams its result back.
          setIsStreaming(true);
          streamingContentRef.current = '';
          setStreamingContent('');
        }
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      stopPing();
      stopReconnect();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, 'Component unmount');
        wsRef.current = null;
      }
    };
  }, [stopPing, stopReconnect]);

  return {
    isConnected,
    isReconnecting,
    isStreaming,
    streamingContent,
    sendMessage,
    sendConfirm,
    connect,
    disconnect,
  };
}
